import React from 'react';
import { useHistory } from 'react-router-dom';
import { Card, CardContent, CardActionArea, Grid, Typography, makeStyles, Box } from '@material-ui/core';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import HomeIcon from '@material-ui/icons/Home';
import FavoriteIcon from '@material-ui/icons/Favorite';
import PsychologyIcon from '@material-ui/icons/EmojiObjects';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AccessibilityNewIcon from '@material-ui/icons/AccessibilityNew';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';
import BackButton from '../common/BackButton';
import { RecordCounts, RecordType, RecordTypeNames } from '../../constants/Records';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  userInfo: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  cardGrid: {
    marginTop: theme.spacing(2),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  cardContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  icon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  count: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
}));

interface RecordTypeSelectProps {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  counts: RecordCounts;
  onFetchCounts: (uniqueId: string) => void;
}

const RecordTypeSelect: React.FC<RecordTypeSelectProps> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  counts,
  onFetchCounts,
}) => {
  const classes = useStyles();
  const history = useHistory();

  // コンテナ側で初期化時にfetchするため、ここでは呼び出し不要
  // リロード用にボタンを押した時のみonFetchCountsを使用

  const getIcon = (type: RecordType) => {
    switch (type) {
      case 'moffcheck':
        return <CheckCircleIcon className={classes.icon} />;
      case 'mofftraining':
        return <DirectionsRunIcon className={classes.icon} />;
      case 'yoga':
        return <FitnessCenterIcon className={classes.icon} />;
      case 'homevisit':
        return <HomeIcon className={classes.icon} />;
      case 'interest':
        return <FavoriteIcon className={classes.icon} />;
      case 'mci':
        return <PsychologyIcon className={classes.icon} />;
      case 'lifecheck':
        return <AssessmentIcon className={classes.icon} />;
      case 'barthel':
        return <AccessibilityNewIcon className={classes.icon} />;
      default:
        return null;
    }
  };

  const getPath = (type: RecordType): string => {
    if (type === 'moffcheck') {
      // MoffCheckは既存のルートに遷移
      return `/institutions/${institutionId}/select_month/${userId}`;
    }
    return `/institutions/${institutionId}/users/${userId}/records/${type}`;
  };

  const handleCardClick = (type: RecordType) => {
    history.push(getPath(type));
  };

  // 表示順序: モフトレチェック, モフトレ, バーセルインデックス, 生活機能チェック, 興味関心チェック, 訪問記録, MCIテスト, ADLヨガ
  const recordTypes: RecordType[] = [
    'moffcheck',
    'mofftraining',
    'barthel',
    'lifecheck',
    'interest',
    'homevisit',
    'mci',
    'yoga',
  ];

  return (
    <div className={classes.root}>
      <BackButton />

      <Box className={classes.userInfo}>
        <Typography variant="subtitle1" color="textSecondary">
          施設名: {institutionName}
        </Typography>
        <Typography variant="h5">利用者: {userName} 様</Typography>
      </Box>

      <Typography variant="h6" className={classes.header}>
        レコード種別を選択してください
      </Typography>

      <Grid container spacing={3} className={classes.cardGrid}>
        {recordTypes.map(type => (
          <Grid item xs={12} sm={6} md={4} key={type}>
            <Card className={classes.card}>
              <CardActionArea onClick={() => handleCardClick(type)} style={{ height: '100%' }}>
                <CardContent className={classes.cardContent}>
                  {getIcon(type)}
                  <Typography variant="h6" component="h2" align="center">
                    {RecordTypeNames[type]}
                  </Typography>
                  {/* モフトレチェック・モフトレは件数取得に複数リクエストが必要なため非表示 */}
                  {type !== 'moffcheck' &&
                    type !== 'mofftraining' &&
                    (counts.loading ? (
                      <Typography variant="body2" className={classes.count}>
                        (読込中...)
                      </Typography>
                    ) : (
                      <Typography variant="body2" className={classes.count}>
                        ({counts[type]}件)
                      </Typography>
                    ))}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default RecordTypeSelect;
